import React, { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  useMediaQuery,
  Autocomplete,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { assetGroupList } from "../../redux/actions/businessFunctions";

export default function AssetGroupDialog({
  open,
  onClose,
  onSubmit,
  bfId,
  orgaId,
  getDescriptionForName = () => "",
  extraOptions = [],
  excludeNames = [],
}) {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();

  // Fetch asset groups for the selected BF/org when dialog opens
  useEffect(() => {
    if (!open) return;
    if (!bfId || !orgaId) return;
    dispatch(assetGroupList(bfId, orgaId));
  }, [open, bfId, orgaId, dispatch]);

  // Read asset groups from Redux and derive names
  const assetGroups = useSelector(
    (state) => state.businessFunctions.assetGroups || []
  );
  const assetGroupNames = useMemo(() => {
    const list = Array.isArray(assetGroups)
      ? assetGroups
      : Array.isArray(assetGroups?.asset_groups)
      ? assetGroups.asset_groups
      : [];
    const allNames = list.map((g) => g?.name).filter(Boolean);
    const extraNames = Array.isArray(extraOptions)
      ? extraOptions.map((g) => g?.name).filter(Boolean)
      : [];
    const excluded = new Set(
      (excludeNames || [])
        .map((n) => (typeof n === "string" ? n.trim().toLowerCase() : ""))
        .filter(Boolean)
    );
    const combined = [...allNames, ...extraNames];
    const seen = new Set();
    return combined
      .filter((n) => {
        const key = (n || "").trim().toLowerCase();
        if (!key) return false;
        if (excluded.has(key)) return false;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((n) => String(n));
  }, [assetGroups, excludeNames, extraOptions]);

  const [name, setName] = useState("");
  const [value, setValue] = useState("10");
  const [justification, setJustification] = useState("");
  const [description, setDescription] = useState("");

  const isValidValue = useMemo(() => /^(?:[1-9]|10)$/.test(value), [value]);

  const handleClose = () => {
    onClose?.();
    // reset local state when closing
    setName("");
    setValue("10");
    setJustification("");
    setDescription("");
  };

  const handleAdd = () => {
    if (!name.trim() || !isValidValue) return;
    // Derive selected asset group ID from Redux list by name
    const list = Array.isArray(assetGroups)
      ? assetGroups
      : Array.isArray(assetGroups?.asset_groups)
      ? assetGroups.asset_groups
      : [];
    const match = list.find((g) => g?.name === name.trim());
    const extraMatch = Array.isArray(extraOptions)
      ? extraOptions.find((g) => g?.name === name.trim())
      : null;
    const selectedAssetGroupId = match?.id ?? extraMatch?.id ?? null;
    onSubmit?.({
      name: name.trim(),
      value,
      justification: justification.trim(),
      description: description.trim(),
      assetGroupId: selectedAssetGroupId,
    });
    // Parent will close; also reset here for safety
    setName("");
    setValue("10");
    setJustification("");
    setDescription("");
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      fullScreen={isSm}
      maxWidth="md"
    >
      <DialogTitle
        sx={{
          fontSize: { xs: "1.1rem", sm: "1.35rem" },
          fontWeight: 600,
          pb: 0.5,
        }}
      >
        Add Asset Group
      </DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          pt: 3,
          pb: 1,
          minWidth: { xs: "auto", sm: 500 },
        }}
      >
        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              mb: 1,
              fontSize: { xs: ".8rem", sm: ".9rem" },
              textTransform: "uppercase",
              letterSpacing: ".06em",
              color: "text.secondary",
            }}
          ></Typography>
          <Autocomplete
            autoHighlight
            // openOnFocus
            options={assetGroupNames}
            value={name}
            onChange={(e, val) => {
              const nextName = val || "";
              setName(nextName);
              setDescription(getDescriptionForName(nextName) || "");
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Name"
                autoFocus
                size="medium"
                InputLabelProps={{
                  sx: { fontSize: { xs: "0.85rem", sm: "0.95rem" } },
                }}
              />
            )}
            clearOnBlur={false}
            disableClearable
            fullWidth
          />
        </Box>
        <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2}>
          <TextField
            label="Value (1-10)"
            type="number"
            inputProps={{
              min: 1,
              max: 10,
              step: 1,
              inputMode: "numeric",
              pattern: "[0-9]*",
            }}
            value={value}
            onChange={(e) => {
              const val = e.target.value;
              if (/^$|^(?:[1-9]|10)$/.test(val)) {
                setValue(val);
              }
            }}
            onKeyDown={(e) => {
              if (["e", "E", "+", "-", ".", ","].includes(e.key)) {
                e.preventDefault();
              }
            }}
            size="medium"
            fullWidth
          />
        </Box>
        <Box>
          <TextField
            label="Justification (Optional)"
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            multiline
            minRows={2}
            maxRows={4}
            size="medium"
            fullWidth
            placeholder="Enter justification for this asset group value..."
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button size="medium" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          size="medium"
          variant="contained"
          disabled={!name.trim() || !isValidValue}
          onClick={handleAdd}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}
